import React, { useState } from 'react';
import { getAuthToken } from '../../../utils/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketingModule = ({ user }) => {
  const [activeSection, setActiveSection] = useState('avatar');
  const [formData, setFormData] = useState({
    businessName: '',
    product: '',
    industry: '',
    targetDescription: ''
  });
  const [generating, setGenerating] = useState(false);
  const [results, setResults] = useState({});

  const sections = [
    { id: 'avatar', name: 'Avatar Client', icon: '👤', desc: 'Définir votre client idéal' },
    { id: 'pain-points', name: 'Points de Douleur', icon: '😰', desc: 'Identifier les problèmes' },
    { id: 'objections', name: 'Objections', icon: '🤔', desc: 'Anticiper les freins' },
    { id: 'benefits', name: 'Bénéfices', icon: '✨', desc: 'Arguments de vente' },
    { id: 'usp', name: 'USP', icon: '🎯', desc: 'Proposition unique' },
    { id: 'funnel', name: 'Tunnel de vente', icon: '📊', desc: 'Stratégie complète' }
  ];

  const handleGenerate = async () => {
    if (!formData.product) return;
    setGenerating(true);
    
    try {
      const token = getAuthToken();
      
      const prompts = {
        avatar: `Créer un avatar client détaillé pour: ${formData.product}
          - Données démographiques
          - Psychographie
          - Comportements d'achat
          - Médias consommés
          - Journée type`,
        'pain-points': `Identifier 10 points de douleur majeurs des clients potentiels pour: ${formData.product}
          Pour chaque point de douleur:
          - Description du problème
          - Impact émotionnel
          - Coût de l'inaction`,
        objections: `Lister les 10 objections principales à l'achat de: ${formData.product}
          Pour chaque objection:
          - L'objection
          - La vraie raison derrière
          - La réponse persuasive`,
        benefits: `Lister 15 bénéfices clés de: ${formData.product}
          Format:
          - Caractéristique → Avantage → Bénéfice émotionnel`,
        usp: `Créer 5 propositions de valeur uniques pour: ${formData.product}
          Chaque USP doit être:
          - Claire et concise
          - Différenciante
          - Orientée bénéfice client`,
        funnel: `Créer une stratégie de tunnel de vente pour: ${formData.product}
          Inclure:
          - TOFU: Awareness (contenu, ads)
          - MOFU: Consideration (lead magnets, nurturing)
          - BOFU: Decision (offres, garanties)
          - Rétention et upsell`
      };

      const response = await fetch(`${API_URL}/api/generate-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          content_type: 'blog',
          topic: `Marketing Strategy - ${activeSection}`,
          tone: 'professional',
          word_count: 1000,
          additional_instructions: `
            Entreprise: ${formData.businessName}
            Produit/Service: ${formData.product}
            Industrie: ${formData.industry}
            Description cible: ${formData.targetDescription}
            
            ${prompts[activeSection]}
          `
        })
      });

      if (response.ok) {
        const data = await response.json();
        setResults({...results, [activeSection]: data.content});
      }
    } catch (error) {
      console.error('Marketing generation error:', error);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6">
      {/* Sections */}
      <div className="col-span-3 space-y-3">
        <h3 className="text-white font-semibold mb-4">Assistant Marketing</h3>
        {sections.map(section => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`w-full text-left p-4 rounded-xl transition-all ${
              activeSection === section.id
                ? 'bg-orange-500/20 border border-orange-500/30'
                : 'bg-[#12121a] border border-gray-800 hover:border-gray-700'
            }`}
          >
            <div className="flex items-center space-x-3">
              <span className="text-2xl">{section.icon}</span>
              <div>
                <p className={`font-medium ${activeSection === section.id ? 'text-orange-400' : 'text-white'}`}>
                  {section.name}
                </p>
                <p className="text-xs text-gray-500">{section.desc}</p>
              </div>
            </div>
            {results[section.id] && (
              <span className="mt-2 inline-block text-xs text-green-400">✓ Généré</span>
            )}
          </button>
        ))}
      </div>

      {/* Configuration */}
      <div className="col-span-4 bg-[#12121a] rounded-2xl border border-gray-800 p-6">
        <h3 className="text-white font-semibold mb-6">Informations Business</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">Nom de l'entreprise</label>
            <input
              type="text"
              value={formData.businessName}
              onChange={(e) => setFormData({...formData, businessName: e.target.value})}
              placeholder="Ex: FitPro Academy"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Produit / Service *</label>
            <input
              type="text"
              value={formData.product}
              onChange={(e) => setFormData({...formData, product: e.target.value})}
              placeholder="Ex: Programme de coaching fitness en ligne"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Industrie</label>
            <input
              type="text"
              value={formData.industry}
              onChange={(e) => setFormData({...formData, industry: e.target.value})}
              placeholder="Ex: Fitness, Bien-être, Coaching"
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 transition-all"
            />
          </div>

          <div>
            <label className="block text-sm text-gray-400 mb-2">Description de la cible</label>
            <textarea
              value={formData.targetDescription}
              onChange={(e) => setFormData({...formData, targetDescription: e.target.value})}
              placeholder="Décrivez votre cible idéale..."
              rows={3}
              className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:border-orange-500 transition-all resize-none"
            />
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating || !formData.product}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 disabled:from-gray-600 disabled:to-gray-700 rounded-xl font-semibold text-white transition-all"
          >
            {generating ? 'Analyse en cours...' : `Générer ${sections.find(s => s.id === activeSection)?.name}`}
          </button>
        </div>
      </div>

      {/* Output */}
      <div className="col-span-5 bg-[#12121a] rounded-2xl border border-gray-800 overflow-hidden flex flex-col">
        <div className="p-4 border-b border-gray-800">
          <h3 className="text-white font-semibold">
            {sections.find(s => s.id === activeSection)?.icon} {sections.find(s => s.id === activeSection)?.name}
          </h3>
        </div>
        <div className="flex-1 p-4 overflow-y-auto">
          {generating ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="w-12 h-12 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4"></div>
                <p>Analyse marketing en cours...</p>
              </div>
            </div>
          ) : results[activeSection] ? (
            <div className="prose prose-invert max-w-none">
              <div className="whitespace-pre-wrap text-gray-300">{results[activeSection]}</div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-500">
              <div className="text-center">
                <span className="text-4xl mb-4 block">{sections.find(s => s.id === activeSection)?.icon}</span>
                <p>Remplissez les informations<br/>et lancez l'analyse</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketingModule;
